import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import * as moment from 'moment-timezone';

import { I18nService } from '../../../../../../i18n/services';
import { ICountry, IGender } from '../../../../../../services/clusters';
import { FormsService } from '../../../../../../services/forms';
import { TimezonesService } from '../../../../../../services/timezones/timezones.service';
import { IRole, IUserSearchFields, UsersService } from '../../../../../../services/users';
import { ISearchSelectData } from '../../../../../../shared/components';

@Component({
	selector: 'app-users-search-form',
	templateUrl: './users-search-form.component.html',
	styleUrls: ['./users-search-form.component.scss']
})
export class UsersSearchFormComponent implements OnInit {
	@Output() validChange = new EventEmitter<boolean>();
	@Output() searchFormValuesChange = new EventEmitter<IUserSearchFields>();
	@Output() searchUsers = new EventEmitter();

	@Input() valid = true;
	@Input() countries: ICountry[] = [];
	@Input() genders: IGender[] = [];
	@Input() roles: IRole[] = [];
	@Input() searchFormValues: IUserSearchFields;

	public textFilters: ISearchSelectData[] = [
		{
			name: this.i18nService.get('Email', 'app.descriptor.users.fields.email'),
			id: 'email',
			meta: {
				type: {
					name: 'text',
					hide: true
				}
			}
		},
		{
			name: this.i18nService.get('Timezone', 'app.descriptor.users.fields.timezone'),
			id: 'timezone',
			meta: {
				type: {
					name: 'text',
					hide: true
				}
			}
		},
		{
			name: this.i18nService.get('Locale', 'app.descriptor.users.fields.locale'),
			id: 'locale',
			meta: {
				type: {
					name: 'text',
					hide: true
				}
			}
		},
		{
			name: this.i18nService.get('First Name', 'app.descriptor.users.fields.firstname'),
			id: 'firstname',
			meta: {
				type: {
					name: 'text',
					hide: true
				}
			}
		},
		{
			name: this.i18nService.get('Last Name', 'app.descriptor.users.fields.lastname'),
			id: 'lastname',
			meta: {
				type: {
					name: 'text',
					hide: true
				}
			}
		},
		{
			name: this.i18nService.get('UUID', 'app.descriptor.users.fields.uuid'),
			id: 'uuid',
			meta: {
				type: {
					name: 'text',
					hide: true
				}
			}
		},
		{
			name: this.i18nService.get('Client ID', 'app.descriptor.users.fields.clientId'),
			id: 'clientId',
			meta: {
				type: {
					name: 'text',
					hide: true
				}
			}
		},
		{
			name: this.i18nService.get('State', 'app.descriptor.users.fields.state'),
			id: 'state',
			meta: {
				type: {
					name: 'text',
					hide: true
				}
			}
		},
		{
			name: this.i18nService.get('Locality', 'app.descriptor.users.fields.locality'),
			id: 'locality',
			meta: {
				type: {
					name: 'text',
					hide: true
				}
			}
		},
		{
			name: this.i18nService.get('Country', 'app.descriptor.users.fields.country'),
			id: 'country',
			meta: {
				type: {
					name: 'text',
					hide: true
				}
			}
		}
	];
	public fieldFilters: ISearchSelectData[] = [
		{
			name: 'Verification Status',
			id: 'emailVerified',
			meta: {
				type: {
					name: 'field',
					hide: true
				}
			}
		},
		{
			name: 'Gender',
			id: 'gender',
			meta: {
				type: {
					name: 'field',
					hide: true
				}
			}
		},
		{
			name: 'Role',
			id: 'role',
			meta: {
				type: {
					name: 'field',
					hide: true
				}
			}
		},
		{
			name: 'Billing Country',
			id: 'billingCountry',
			meta: {
				type: {
					name: 'field',
					hide: true
				}
			}
		},
		{
			name: 'Last Login',
			id: 'lastLogin',
			meta: {
				type: {
					name: 'field',
					hide: true
				}
			}
		},
		{
			name: 'Created',
			id: 'createdTime',
			meta: {
				type: {
					name: 'field',
					hide: true
				}
			}
		}
	];

	public selectedTextFilters: ISearchSelectData[] = [];
	public selectedFieldFilters: ISearchSelectData[] = [];
	public availableFilters: ISearchSelectData[] = [...this.textFilters, ...this.fieldFilters];

	public expandedTimezoneArea: any;

	public searchForm = this.formBuilder.group({
		uuidControl: ['', [this.formsService.minLength(2, true)]],
		emailControl: ['', [this.formsService.minLength(2, true)]],
		clientIdControl: [''],
		lastnameControl: [''],
		firstnameControl: [''],
		stateControl: [''],
		localityControl: [''],
		countryControl: [''],
		timezoneControl: [''],
		localeControl: [''],
		gender: ['all'],
		role: ['all'],
		billingCountry: ['all'],
		emailVerified: ['all'],
		loginStartDateControl: [{ value: null, disabled: true }],
		loginEndDateControl: [{ value: null, disabled: true }],
		loginTimezone: ['Australia/Sydney'],
		createdStartDateControl: [{ value: null, disabled: true }],
		createdEndDateControl: [{ value: null, disabled: true }],
		createdTimezone: ['Australia/Sydney']
	});

	constructor(
		private formBuilder: FormBuilder,
		private formsService: FormsService,
		private i18nService: I18nService,
		private usersService: UsersService,
		public timezonesService: TimezonesService
	) {}

	public ngOnInit(): void {
		this.getTimezones();
		this.initSearch();

		this.searchForm.statusChanges.subscribe((status) => {
			this.valid = status === 'VALID';
			this.validChange.emit(this.valid);
		});
	}

	private initSearch(): void {
		const searchQuery = this.usersService.userSearchQuery;

		if (searchQuery.emailVerified && searchQuery.emailVerified !== 'all') {
			this.searchForm.controls.emailVerified.setValue(searchQuery.emailVerified);
			this.addFieldFilters('emailVerified');
		}

		if (searchQuery.gender && searchQuery.gender !== 'all') {
			this.searchForm.controls.gender.setValue(searchQuery.gender);
			this.addFieldFilters('gender');
		}

		if (searchQuery.role && searchQuery.role !== 'all') {
			this.searchForm.controls.role.setValue(searchQuery.role);
			this.addFieldFilters('role');
		}

		if (searchQuery.billingCountry && searchQuery.billingCountry !== 'all') {
			this.searchForm.controls.billingCountry.setValue(searchQuery.billingCountry);
			this.addFieldFilters('billingCountry');
		}

		if (Object.keys(searchQuery.query).length > 0) {
			for (const [key, value] of Object.entries(searchQuery.query)) {
				this.searchForm.get(`${key}Control`).setValue(value);
				this.addTextFilters(key);
			}
		} else {
			this.addTextFilters('email');
		}

		if (searchQuery.loginTimezone) {
			this.searchForm.controls.loginTimezone.setValue(searchQuery.loginTimezone);
		}

		if (searchQuery.createdTimezone) {
			this.searchForm.controls.createdTimezone.setValue(searchQuery.createdTimezone);
		}

		if (searchQuery.ranges && searchQuery.ranges.hasOwnProperty('lastLogin')) {
			this.searchForm.controls.loginStartDateControl.setValue(
				searchQuery.ranges['lastLogin'][0] ? new Date(searchQuery.ranges['lastLogin'][0]) : null
			);
			this.searchForm.controls.loginEndDateControl.setValue(
				searchQuery.ranges['lastLogin'][1] ? new Date(searchQuery.ranges['lastLogin'][1]) : null
			);
			this.addFieldFilters('lastLogin');
		}

		if (searchQuery.ranges && searchQuery.ranges.hasOwnProperty('createdTime')) {
			this.searchForm.controls.createdStartDateControl.setValue(
				searchQuery.ranges['createdTime'][0] ? new Date(searchQuery.ranges['createdTime'][0]) : null
			);
			this.searchForm.controls.createdEndDateControl.setValue(
				searchQuery.ranges['createdTime'][1] ? new Date(searchQuery.ranges['createdTime'][1]) : null
			);
			this.addFieldFilters('createdTime');
		}

		this.getSearchFormValues();
	}

	public editTextFilters(selected: string | number, previous: ISearchSelectData): void {
		if (selected !== previous.id) {
			const index = this.selectedTextFilters.findIndex((selectedFilter) => selectedFilter.id === previous.id);
			this.searchForm.controls[`${selected}Control`].setValue(this.searchForm.controls[`${previous.id}Control`].value);
			this.searchForm.controls[`${previous.id}Control`].setValue('');
			this.selectedTextFilters[index] = this.textFilters.find((filter) => filter.id === selected);
			this.updateTextFiltersList();
		}
	}

	private addTextFilters(id: string | number): void {
		this.selectedTextFilters = [this.textFilters.find((filter) => filter.id === id), ...this.selectedTextFilters].sort((a, b) =>
			a.name.localeCompare(b.name)
		);
		this.updateTextFiltersList();
	}

	private addFieldFilters(id: string | number): void {
		this.selectedFieldFilters = [this.fieldFilters.find((filter) => filter.id === id), ...this.selectedFieldFilters].sort((a, b) =>
			a.name.localeCompare(b.name)
		);
		this.setAvailableFilters();
	}

	private setAvailableFilters(): void {
		const selectedTextFilters = this.selectedTextFilters.map((filter) => filter.id);
		const selectedFieldFilters = this.selectedFieldFilters.map((filter) => filter.id);

		this.availableFilters = [
			...this.textFilters.filter((filter) => !selectedTextFilters.includes(filter.id)),
			...this.fieldFilters.filter((filter) => !selectedFieldFilters.includes(filter.id))
		];
	}

	private updateTextFiltersList(): void {
		this.setAvailableFilters();
		const selectedFilters = this.selectedTextFilters.map((filter) => filter.id);

		this.selectedTextFilters = this.selectedTextFilters.map((filter) => {
			filter.meta['filters'] = {
				name: 'list',
				hide: true,
				data: [filter, ...this.textFilters.filter((textFilter) => !selectedFilters.includes(textFilter.id))]
			};

			return filter;
		});
	}

	public addFilter(filter: string): void {
		const textFilter = this.textFilters.find((availableFilter) => availableFilter.id === filter);
		const fieldFilter = this.fieldFilters.find((availableFilter) => availableFilter.id === filter);

		if (textFilter?.meta?.type?.name === 'text') {
			this.addTextFilters(filter);
		} else if (fieldFilter?.meta?.type?.name === 'field') {
			this.addFieldFilters(filter);
		}
	}

	public removeTextFilter(index: number): void {
		this.searchForm.controls[`${this.selectedTextFilters[index].id}Control`].setValue('');
		this.selectedTextFilters.splice(index, 1);
		this.updateTextFiltersList();
	}

	public removeFieldFilter(event: MouseEvent, filter: string) {
		const index = this.selectedFieldFilters.findIndex((selectedFilter) => selectedFilter.id === filter);
		this.selectedFieldFilters.splice(index, 1);
		this.clearFilter(event, filter);
		this.setAvailableFilters();
	}

	public clearFilter(event: MouseEvent, filter: string): void {
		switch (filter) {
			case 'emailVerified':
				this.searchForm.controls.emailVerified.setValue('all');
				break;
			case 'gender':
				this.searchForm.controls.gender.setValue('all');
				break;
			case 'role':
				this.searchForm.controls.role.setValue('all');
				break;
			case 'billingCountry':
				this.searchForm.controls.billingCountry.setValue('all');
				break;
			case 'lastLogin':
				this.searchForm.controls.loginStartDateControl.setValue(null);
				this.searchForm.controls.loginEndDateControl.setValue(null);
				break;
			case 'loginStartDate':
				this.searchForm.controls.loginStartDateControl.setValue(null);
				break;
			case 'loginEndDate':
				this.searchForm.controls.loginEndDateControl.setValue(null);
				break;
			case 'createdTime':
				this.searchForm.controls.createdStartDateControl.setValue(null);
				this.searchForm.controls.createdEndDateControl.setValue(null);
				break;
			case 'createdStartDate':
				this.searchForm.controls.createdStartDateControl.setValue(null);
				break;
			case 'createdEndDate':
				this.searchForm.controls.createdEndDateControl.setValue(null);
				break;
			default:
				this.searchForm.controls.emailVerified.setValue('all');
				this.searchForm.controls.gender.setValue('all');
				this.searchForm.controls.role.setValue('all');
				this.searchForm.controls.billingCountry.setValue('all');
				this.searchForm.controls.loginStartDateControl.setValue(null);
				this.searchForm.controls.loginEndDateControl.setValue(null);
				this.searchForm.controls.createdStartDateControl.setValue(null);
				this.searchForm.controls.createdEndDateControl.setValue(null);
				break;
		}

		event.preventDefault();
		event.stopPropagation();
	}

	public getLocalizedDate(date: string | number, displayTime = true): string {
		if (displayTime) {
			return new Date(date).toLocaleString();
		}

		return new Date(date).toLocaleDateString();
	}

	public toggleTimezoneGroup(event: Event, area: string): void {
		this.expandedTimezoneArea = this.expandedTimezoneArea === area ? '' : area;
		event.preventDefault();
	}

	public getLabel(id: string, items: { label: string; id: string }[]): string {
		const item = items.find((value) => value.id === id);

		return item ? item.label : id;
	}

	public getRoleLabel(roleId: string): string {
		const role = this.roles.find((value) => value.roleId === roleId);

		return role?.roleId || roleId;
	}

	public search(): void {
		this.getSearchFormValues();
		this.searchUsers.emit();
	}

	public clearSearch(): void {
		const userSearchQuery = { ...this.usersService.userSearchQuery };

		this.selectedTextFilters = [];
		this.selectedFieldFilters = [];
		this.addTextFilters('email');

		userSearchQuery.query = {};
		userSearchQuery.emailVerified = 'all';
		userSearchQuery.billingCountry = 'all';
		userSearchQuery.gender = 'all';
		userSearchQuery.role = 'all';
		userSearchQuery.ranges = {};
		userSearchQuery.loginTimezone = 'Australia/Sydney';
		userSearchQuery.createdTimezone = 'Australia/Sydney';

		this.usersService.userSearchQuery = userSearchQuery;

		for (const value of this.textFilters) {
			this.searchForm.get(`${value.id}Control`).setValue('', { emitEvent: false });
		}

		this.searchForm.controls.emailVerified.setValue(userSearchQuery.emailVerified, {
			emitEvent: false
		});
		this.searchForm.controls.gender.setValue(userSearchQuery.gender, {
			emitEvent: false
		});
		this.searchForm.controls.role.setValue(userSearchQuery.role, {
			emitEvent: false
		});
		this.searchForm.controls.billingCountry.setValue(userSearchQuery.billingCountry, {
			emitEvent: false
		});

		this.searchForm.controls.loginStartDateControl.setValue(null, { emitEvent: false });
		this.searchForm.controls.loginEndDateControl.setValue(null, { emitEvent: false });

		this.searchForm.controls.createdStartDateControl.setValue(null, { emitEvent: false });
		this.searchForm.controls.createdEndDateControl.setValue(null, { emitEvent: false });

		this.searchForm.controls.loginTimezone.setValue(userSearchQuery.loginTimezone, {
			emitEvent: false
		});
		this.searchForm.controls.createdTimezone.setValue(userSearchQuery.createdTimezone, {
			emitEvent: false
		});

		this.getSearchFormValues();
	}

	private getTimezones(): void {
		this.timezonesService.getTimezones().subscribe(() => {
			try {
				const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
				this.searchForm.controls.loginTimezone.setValue(
					this.usersService.userSearchQuery.loginTimezone !== 'Australia/Sydney'
						? this.usersService.userSearchQuery.loginTimezone
						: this.timezonesService.data.timezones.findIndex((timezone) => timezone.id === browserTimezone) > -1
						? browserTimezone
						: this.usersService.userSearchQuery.loginTimezone
				);

				this.searchForm.controls.createdTimezone.setValue(
					this.usersService.userSearchQuery.createdTimezone !== 'Australia/Sydney'
						? this.usersService.userSearchQuery.createdTimezone
						: this.timezonesService.data.timezones.findIndex((timezone) => timezone.id === browserTimezone) > -1
						? browserTimezone
						: this.usersService.userSearchQuery.createdTimezone
				);
			} catch (e) {}
		});
	}

	private getSearchFormValues(): void {
		const values: IUserSearchFields = {
			query: {},
			ranges: {},
			createdTimezone: '',
			loginTimezone: '',
			emailVerified: '',
			gender: '',
			role: '',
			billingCountry: ''
		};

		this.selectedTextFilters.forEach((val) => {
			const value = this.searchForm.get(`${val.id}Control`).value.trim();
			if (value) {
				values.query[val.id] = value;
			}
		});

		this.selectedFieldFilters.forEach((val) => {
			const value = this.searchForm.get(`${val.id}`)?.value?.trim();
			if (value) {
				values[val.id] = value;
			}
		});

		values.loginTimezone = this.searchForm.controls.loginTimezone.value;
		values.createdTimezone = this.searchForm.controls.createdTimezone.value;

		const loginStartDate = moment
			.tz(new Date(this.searchForm.controls.loginStartDateControl.value).toISOString(), this.searchForm.controls.loginTimezone.value)
			.startOf('day')
			.format('x');
		const loginEndDate = moment
			.tz(new Date(this.searchForm.controls.loginEndDateControl.value).toISOString(), this.searchForm.controls.loginTimezone.value)
			.endOf('day')
			.format('x');

		const lastLogin =
			this.searchForm.controls.loginStartDateControl.value && this.searchForm.controls.loginEndDateControl.value
				? [loginStartDate, loginEndDate]
				: this.searchForm.controls.loginStartDateControl.value
				? [loginStartDate]
				: this.searchForm.controls.loginEndDateControl.value
				? [null, loginEndDate]
				: null;

		const createdStartDate = moment
			.tz(
				new Date(this.searchForm.controls.createdStartDateControl.value).toISOString(),
				this.searchForm.controls.createdTimezone.value
			)
			.startOf('day')
			.format('x');
		const createdEndDate = moment
			.tz(
				new Date(this.searchForm.controls.createdEndDateControl.value).toISOString(),
				this.searchForm.controls.createdTimezone.value
			)
			.endOf('day')
			.format('x');

		const createdTime =
			this.searchForm.controls.createdStartDateControl.value && this.searchForm.controls.createdEndDateControl.value
				? [createdStartDate, createdEndDate]
				: this.searchForm.controls.createdStartDateControl.value
				? [createdStartDate]
				: this.searchForm.controls.createdEndDateControl.value
				? [null, createdEndDate]
				: null;

		if (lastLogin) {
			values.ranges['lastLogin'] = lastLogin;
		}

		if (createdTime) {
			values.ranges['createdTime'] = createdTime;
		}

		this.searchFormValues = values;
		this.searchFormValuesChange.emit(values);
	}
}
